import { useCombineFetchedOptions } from 'modules/queryBuilder/renderers/hooks/useCombineFetchedOptions';
import { TriggerHoldUntilReplyRuleType } from 'modules/workflows/constants/nodes/holdUntilReply';
import { useEffect, useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import { useDebouncedState } from 'utils/hooks/useDebouncedState';
import { CAMPAIGN_ID, TRIGGER_RULE_TYPE } from '../HoldUntilReply.constants';
import { FormFieldLabel } from '../HoldUntilReply.styles';
import { useHoldUntilReplyContext } from '../HoldUntilReplyContext';
import { useCampaignOptions } from '../hooks/useCampaignOptions';
import {
  CampaignSelectorContainer,
  CampaignSelectorHeader,
} from './WhatsAppCampaignSelector.style';
import { WhatsAppFilterSelectField } from './WhatsAppFilterSelectField';

const bulletOperator = '\u{2219}';

export const WHATSAPP_CAMPAIGN_SELECT_TEST_ID = 'whatsapp-campaign-select';

export const WhatsAppCampaignSelector = ({ isReadOnly = false }: { isReadOnly?: boolean }) => {
  const [campaignId, triggerRuleType] = useWatch({ name: [CAMPAIGN_ID, TRIGGER_RULE_TYPE] });
  const [debouncedSearch, searchInputValue, setSearchInputValue] = useDebouncedState('', 500);

  const {
    options,
    selectedOptions,
    isSelectedCampaignsLoading,
    isSuggestedCampaignsLoading,
    onFetchMoreCampaigns,
    selectedCampaignPage,
    suggestedCampaignPage,
    resetCampaignPages,
    campaignsCount,
  } = useCampaignOptions({
    search: debouncedSearch,
    values: !!campaignId ? [campaignId] : null,
  });

  const selectedCampaign = useMemo(
    () => selectedOptions?.find((x) => x.value === campaignId),
    [campaignId, selectedOptions],
  );

  const { fetchWhatsAppCampaignQuickReplies } = useHoldUntilReplyContext();

  useEffect(() => {
    if (selectedCampaign?.value) {
      const templateId = selectedCampaign?.templateId;
      fetchWhatsAppCampaignQuickReplies(templateId);
    }
  }, [selectedCampaign?.value, selectedCampaign?.templateId]);

  const { combinedOptions: combinedSuggestedOptions, resetCombinedOptions: resetSuggestedOptions } =
    useCombineFetchedOptions({
      options,
      page: suggestedCampaignPage,
      isLoading: isSuggestedCampaignsLoading,
    });

  const { combinedOptions: combinedSelectedOptions, resetCombinedOptions: resetSelectedOptions } =
    useCombineFetchedOptions({
      options: selectedOptions,
      page: selectedCampaignPage,
      isLoading: isSelectedCampaignsLoading,
    });

  const combinedOptions = useMemo(
    () => [...combinedSelectedOptions, ...combinedSuggestedOptions],
    [combinedSelectedOptions, combinedSuggestedOptions],
  );

  const selectOptions = useMemo(() => {
    return (
      combinedOptions as {
        title: string;
        value: string;
        templateId: number;
      }[]
    ).map((campaign) => ({
      id: `${campaign.value}`,
      label: campaign.title,
      value: `${campaign.value}`,
      description: `ID: ${campaign.value} ${bulletOperator} Template ID: ${campaign.templateId}`,
    }));
  }, [combinedOptions]);

  const handleSetSearch = (newSearch: string) => {
    if (newSearch !== searchInputValue) {
      setSearchInputValue(newSearch);
      resetSuggestedOptions();
      resetSelectedOptions();
      resetCampaignPages();
    }
  };

  return (
    <>
      {(triggerRuleType as TriggerHoldUntilReplyRuleType) ===
        TriggerHoldUntilReplyRuleType.WHATSAPP && (
        <CampaignSelectorContainer>
          <CampaignSelectorHeader>
            <div>
              <FormFieldLabel>WhatsApp campaign</FormFieldLabel> (optional)
            </div>
            <div>Select a campaign to pre-populate quick replies</div>
          </CampaignSelectorHeader>
          <WhatsAppFilterSelectField
            data-test={WHATSAPP_CAMPAIGN_SELECT_TEST_ID}
            onInputChange={handleSetSearch}
            options={selectOptions}
            name={CAMPAIGN_ID}
            placeholder="Select a WhatsApp campaign..."
            isLoading={
              isSelectedCampaignsLoading ||
              isSuggestedCampaignsLoading ||
              (selectOptions?.length === 0 && campaignsCount !== 0)
            }
            isDisabled={isReadOnly}
            isClearable
            isVirtualized
            onMenuScrollToBottom={onFetchMoreCampaigns}
            reset={() => {
              resetSuggestedOptions();
              resetSelectedOptions();
              resetCampaignPages();
            }}
          />

          {selectedCampaign && (
            <div>
              ID: {selectedCampaign?.value} {bulletOperator} Template ID:{' '}
              {selectedCampaign?.templateId}
            </div>
          )}
        </CampaignSelectorContainer>
      )}
    </>
  );
};
